import React, { useMemo } from 'react';
import { CarHistoryTimeline } from '@/components/CarHistoryTimeline/CarHistoryTimeline';
import type { CarHistoryEvent } from '@/components/CarHistoryTimeline/CarHistoryTimeline.types';
import { DataTable } from '@/components/DataTable/DataTable';
import { SectionSubtitle } from '@/components/Typography/SectionSubtitle';
import type { CepikDisplayValue, RaportCepikProps } from './RaportCepik.types';

interface CepikRow {
  label: string;
  value: CepikDisplayValue;
}

interface CepikDataTableProps {
  rows: CepikRow[];
}

interface CepikSectionProps extends CepikDataTableProps {
  id: string;
  title: string;
}

const hasDisplayValue = (value: CepikDisplayValue): value is string => {
  return typeof value === 'string' && value.trim().length > 0;
};

const getVisibleRows = (rows: CepikRow[]): CepikRow[] => {
  return rows.filter(({ value }) => hasDisplayValue(value));
};

const CepikDataTable: React.FC<CepikDataTableProps> = ({ rows }) => (
  <DataTable>
    {rows.map(({ label, value }) => (
      <DataTable.Row id={label} key={label}>
        <DataTable.Column isLabel width="w-1/3">
          {label}
        </DataTable.Column>
        <DataTable.Column>{value}</DataTable.Column>
      </DataTable.Row>
    ))}
  </DataTable>
);

const CepikSection: React.FC<CepikSectionProps> = ({ id, title, rows }) => {
  const visibleRows = getVisibleRows(rows);

  if (visibleRows.length === 0) return null;

  return (
    <div id={id}>
      <SectionSubtitle title={title} />
      <div className="mt-6">
        <CepikDataTable rows={visibleRows} />
      </div>
    </div>
  );
};

export const RaportCepik: React.FC<RaportCepikProps> = ({
  technicalData,
  documents,
  homologation,
  eventsSummary,
  currentStatus,
  timelineEvents,
}) => {
  const carHistoryEvents: CarHistoryEvent[] = useMemo(() => {
    if (!timelineEvents) return [];
    return timelineEvents.map((event) => ({
      id: event.id,
      date: event.date,
      type: event.type,
      title: event.title,
      description: event.description,
      details: event.details,
    }));
  }, [timelineEvents]);

  const technicalRows: CepikRow[] = [
    {
      label: 'Rok produkcji',
      value: technicalData.yearOfProduction,
    },
    {
      label: 'Pojemność silnika (cm3)',
      value: technicalData.engineCapacity,
    },
    {
      label: 'Moc silnika (kW)',
      value: technicalData.enginePower,
    },
    {
      label: 'Paliwo',
      value: technicalData.fuel,
    },
    {
      label: 'Paliwo alternatywne',
      value: technicalData.alternativeFuel,
    },
    {
      label: 'Emisja CO2',
      value: technicalData.co2Emission,
    },
    {
      label: 'Norma euro',
      value: technicalData.euroNorm,
    },
    {
      label: 'Średnie zużycie paliwa na 100 km',
      value: technicalData.averageFuelConsumption,
    },
    {
      label: 'Liczba miejsc ogółem',
      value: technicalData.seatsCount,
    },
    {
      label: 'Liczba miejsc siedzących',
      value: technicalData.passengerSeatsCount,
    },
    {
      label: 'Masa własna pojazdu',
      value: technicalData.vehicleWeight,
    },
    {
      label: 'Maks. masa całk. przyczepy z hamulcem',
      value: technicalData.maxTrailerWeightBraked,
    },
    {
      label: 'Maks. masa całk. przyczepy bez hamulca',
      value: technicalData.maxTrailerWeightUnbraked,
    },
    {
      label: 'Dopuszczalna masa całkowita',
      value: technicalData.permissibleTotalWeight,
    },
    {
      label: 'Liczba osi',
      value: technicalData.axlesCount,
    },
    {
      label: 'Rozstaw osi',
      value: technicalData.wheelbase,
    },
    {
      label: 'Rozstaw kół (średni)',
      value: technicalData.wheelTrack,
    },
  ];

  const documentRows: CepikRow[] = [
    {
      label: 'Data wydania aktualnego dokumentu',
      value: documents.documentIssueDate,
    },
    {
      label: 'Typ dokumentu',
      value: documents.documentType,
    },
    {
      label: 'Stan dokumentu',
      value: documents.documentStatus,
    },
    {
      label: 'Data pierwszej rejestracji',
      value: documents.firstRegistrationDate,
    },
    {
      label: 'VIN',
      value: documents.vin,
    },
  ];

  const homologationRows: CepikRow[] = [
    {
      label: 'Numer',
      value: homologation.homologationNumber,
    },
    {
      label: 'Kategoria',
      value: homologation.category,
    },
    {
      label: 'Wersja',
      value: homologation.version,
    },
    {
      label: 'Wariant',
      value: homologation.variant,
    },
    {
      label: 'Typ',
      value: homologation.type,
    },
  ];

  const eventSummaryRows: CepikRow[] = [
    {
      label: 'Właściciele (od rejestracji do wygenerowania raportu)',
      value: eventsSummary.ownersCount,
    },
  ];

  const currentStatusRows: CepikRow[] = [
    {
      label: 'Województwo, w którym pojazd zarejestrowano',
      value: currentStatus.registrationVoivodeship,
    },
    {
      label: 'Polisa OC',
      value: currentStatus.ocInsurance,
    },
    {
      label: 'Data ważności polisy OC',
      value: currentStatus.ocInsuranceExpiryDate,
    },
    {
      label: 'Badanie techniczne',
      value: currentStatus.technicalInspection,
    },
    {
      label: 'Ostatni stan drogomierza',
      value: currentStatus.lastOdometerReading,
    },
    {
      label: 'Drogomierz cofnięty',
      value: currentStatus.odometerRolledBack,
    },
    {
      label: 'Błąd odczytu drogomierza',
      value: currentStatus.odometerError,
    },
    {
      label: 'Liczba właścicieli',
      value: currentStatus.ownersCount,
    },
    {
      label: 'Liczba współwłaścicieli',
      value: currentStatus.coOwnersCount,
    },
  ];

  const hasVisibleContent =
    [
      technicalRows,
      documentRows,
      homologationRows,
      eventSummaryRows,
      currentStatusRows,
    ].some((rows) => getVisibleRows(rows).length > 0) ||
    carHistoryEvents.length > 0;

  if (!hasVisibleContent) return null;

  return (
    <div className="flex flex-col gap-10 lg:mt-10 lg:gap-16">
      <CepikSection
        id="informacje-o-pojezdzie-dane-techniczne"
        title="Dane techniczne"
        rows={technicalRows}
      />
      <CepikSection
        id="informacje-o-pojezdzie-dokumenty"
        title="Dokumenty"
        rows={documentRows}
      />
      <CepikSection
        id="informacje-o-pojezdzie-swiadectwo-homologacji"
        title="Świadectwo homologacji"
        rows={homologationRows}
      />

      {carHistoryEvents.length > 0 && (
        <div id="informacje-o-pojezdzie-os-czasu">
          <SectionSubtitle title="Oś czasu" />
          <div className="mt-6">
            <CarHistoryTimeline
              events={carHistoryEvents}
              showNoDataGaps={false}
            />
          </div>
        </div>
      )}

      <CepikSection
        id="informacje-o-pojezdzie-podsumowanie-zdarzen"
        title="Podsumowanie zdarzeń"
        rows={eventSummaryRows}
      />
      <CepikSection
        id="informacje-o-pojezdzie-stan-aktualny"
        title="Stan aktualny"
        rows={currentStatusRows}
      />
    </div>
  );
};

export default RaportCepik;
